import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Delete, Edit, Save } from '@material-ui/icons/';
import { StepType } from '../../../api/jurisdiction';
import { linkify } from '../../../utils/linkify';

type Props = {
  data: StepType;
  setData: Function;
  onDelete: Function;
  onSaveTable: Function;
};
const COLLAPSED_LINES = 3;

const DataRowEntry: FC<Props> = ({ data, setData, onDelete, onSaveTable }) => {
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const readOnlyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data && data.id) {
      setIsEditing(false);
    }
  }, []);

  // Only offer "Show more" when the clamped text actually overflows.
  useLayoutEffect(() => {
    const measure = () => {
      const el = readOnlyRef.current;
      if (!el || isExpanded) return;
      setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data.requestOrResult, isEditing, isExpanded]);

  const onChange = e => {
    setData({ ...data, requestOrResult: e.target.value });
  };

  const onEdit = () => {
    if (isEditing) {
      onSaveTable();
    }

    setIsEditing(!isEditing);
  };

  return (
    <Wrapper>
      <Content>
        {isEditing ? (
          <Input type="text" value={data.requestOrResult} onChange={onChange} />
        ) : (
          <>
            <ReadOnly
              ref={readOnlyRef}
              $expanded={isExpanded}
              dangerouslySetInnerHTML={{ __html: linkify(data.requestOrResult) }}
            />
            {(isOverflowing || isExpanded) && (
              <ToggleButton type="button" onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? 'Show less' : 'Show more'}
              </ToggleButton>
            )}
          </>
        )}
      </Content>
      <Actions>
        <ActionButton type="button" title={isEditing ? 'Save' : 'Edit'} onClick={onEdit}>
          {isEditing ? <Save fontSize="small" /> : <Edit fontSize="small" />}
        </ActionButton>
        {data.id && (
          <ActionButton type="button" title="Delete" onClick={() => onDelete(data.id)}>
            <Delete fontSize="small" />
          </ActionButton>
        )}
      </Actions>
    </Wrapper>
  );
};

export default DataRowEntry;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 12px;
  box-sizing: border-box;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReadOnly = styled.div<{ $expanded: boolean }>`
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--mainText);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  box-sizing: border-box;

  ${({ $expanded }) =>
    !$expanded &&
    `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${COLLAPSED_LINES};
    overflow: hidden;
  `}

  a {
    color: var(--primary-700);
  }
`;

const ToggleButton = styled.button`
  margin-top: 4px;
  padding: 2px 0;
  border: none;
  background: none;
  font-family: ${FONT_STACK};
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--primary-600);
  cursor: pointer;

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 4px;
  }
`;

const Input = styled.textarea<{ type: string }>`
  width: 100%;
  min-height: 100px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--mainText);
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  padding-top: 4px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
